/**
 * User level enum for the bakery platform.
 *
 * Covers all roles found in a multi-outlet bakery + dine-in operation.
 * Keep these values in sync with backend/lib/userLevels.js.
 */

export const UserLevel = {
  CUSTOMER: 1, // End customer — online ordering, purchase history
  COUNTER_STAFF: 2, // Counter staff — takes orders, quick-order, search
  ADMIN: 3, // Outlet admin — analytics, user mgmt, settings
  ORDER_CHECKER: 4, // Checker — verifies order quality & completeness
  BAKER: 5, // Baker / kitchen staff — production queue, baking schedule
  CASHIER: 6, // Cashier — billing, payments, POS
  DELIVERY: 7, // Delivery staff — dispatch, delivery tracking
  OUTLET_MANAGER: 8, // Outlet manager — single-outlet oversight + limited analytics
  WAITER: 9, // Waiter — dine-in service, table orders
  SUPER_ADMIN: 10, // Super admin / chain owner — all outlets, full access
};

export const UserLevelMeta = {
  [UserLevel.CUSTOMER]: {
    label: "Customer",
    description:
      "End customer. Can browse products, place online orders, and view purchase history.",
    defaultPath: "/orders",
  },
  [UserLevel.COUNTER_STAFF]: {
    label: "Counter Staff",
    description:
      "Bakery counter staff. Takes counter orders, manages quick orders, searches existing orders.",
    defaultPath: "/quick-order",
  },
  [UserLevel.ADMIN]: {
    label: "Admin",
    description:
      "Outlet administrator. Access to analytics dashboard, user management, and settings.",
    defaultPath: "/dashboard",
  },
  [UserLevel.ORDER_CHECKER]: {
    label: "Order Checker",
    description:
      "Quality control staff. Verifies order correctness and completeness before dispatch.",
    defaultPath: "/order-checker",
  },
  [UserLevel.BAKER]: {
    label: "Baker",
    description:
      "Kitchen / production staff. Manages the baking queue and production schedule.",
    defaultPath: "/queue",
  },
  [UserLevel.CASHIER]: {
    label: "Cashier",
    description:
      "Billing staff. Handles payments, invoices, and POS operations.",
    defaultPath: "/order",
  },
  [UserLevel.DELIVERY]: {
    label: "Delivery Staff",
    description:
      "Delivery and dispatch staff. Manages delivery orders and tracks shipments.",
    defaultPath: "/order",
  },
  [UserLevel.OUTLET_MANAGER]: {
    label: "Outlet Manager",
    description:
      "Single-outlet manager. Oversees staff, daily operations, and outlet-level analytics.",
    defaultPath: "/dashboard",
  },
  [UserLevel.WAITER]: {
    label: "Waiter",
    description:
      "Dine-in service staff. Takes table orders and manages dine-in sessions.",
    defaultPath: "/consumer",
  },
  [UserLevel.SUPER_ADMIN]: {
    label: "Super Admin",
    description:
      "Chain / company owner. Full access to all outlets, global analytics, and system configuration.",
    defaultPath: "/dashboard",
  },
};
